import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {RangeComponent} from "../../../../components/Range/RangeComponent"
import iconSound from "../../../../assets/images/icons/settings/sound.png"
import iconSoundOff from "../../../../assets/images/icons/settings/soundoff.png"
import microphoneOn from "../../../../assets/images/icons/settings/microphoneon.png"
import microphoneOff from "../../../../assets/images/icons/settings/microphoneoff.png"
import playSound from "../../../../assets/images/icons/media/play-sound.png"
import saveSound from "../../../../assets/images/icons/media/save-sound.png"
import {useDispatch, useSelector} from "react-redux"
import {useRPC} from "../../../../hooks/rpc"
import {setSoundStateAction} from "../../../../store/control/actions"
import {useMessage} from "../../../../hooks/message"
import {useLocalization} from "../../../../hooks/localization"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {useQuestion} from "../../../../hooks/question"
import {useLocalStorage} from "../../../../hooks/use-storage"
import "./index.scss"

export const SoundEditScreen = memo(function SoundEditScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const dispatch = useDispatch()
    const question = useQuestion()
    const {failure, success} = useMessage()
    const sound = useSelector(state => state.control.sound)

    const incoming_call_outer_panel = useMemo<SelectItem<number>[]>(
        () => [
            {
                value: 5,
                label: t("bell_0")
            },
            {
                value: 6,
                label: t("bell_1")
            },
            {
                value: 7,
                label: t("bell_2")
            },
            {
                value: 8,
                label: t("bell_3")
            },
            {
                value: 9,
                label: t("bell_4")
            }
        ],
        [t]
    )

    const incoming_call_flat_front = useMemo<SelectItem<number>[]>(
        () => [
            {
                value: 20,
                label: t("sip_bell_0")
            },
            {
                value: 21,
                label: t("sip_bell_1")
            },
            {
                value: 22,
                label: t("sip_bell_2")
            },
            {
                value: 23,
                label: t("sip_bell_3")
            },
            {
                value: 24,
                label: t("sip_bell_4")
            },
            {
                value: 25,
                label: t("sip_bell_5")
            },
            {
                value: 26,
                label: t("sip_bell_6")
            }
        ],
        [t]
    )
    const [bellOption, setBellOption] = useLocalStorage("bellOpt1", incoming_call_outer_panel[0])
    const [bellOption2, setBellOption2] = useLocalStorage("bellOpt2", incoming_call_flat_front[0])

    const [soundReverse, setSoundReverse] = useState(sound?.click_sound || false)
    const [soundIncomingCallOutsidePanel, setSoundIncomingCallOutsidePanel] = useState(
        sound?.incoming_call_outside_panel_level || 50
    )
    const [soundIncomingCallFlatFrontLevel, setSoundIncomingCallFlatFrontLevel] = useState(
        sound.incoming_call_flat_front_level || 50
    )
    const [microphoneLevel, setMicrophoneLevel] = useState(sound?.microphone_level || 50)
    const [intercomLevel, setIntercomLevel] = useState(sound?.intercom_level || 50)
    const [soundAlarmLevel, setSoundAlarmLevel] = useState(sound.alarm_level || 50)

    const [selectedSound, setSelectedSound] = useState<SelectItem<number>>(
        incoming_call_flat_front.find(option => option?.value === bellOption?.value)
    )
    const [selectedSound2, setSelectedSound2] = useState<SelectItem<number>>(
        incoming_call_outer_panel.find(option => option?.value === bellOption2?.value)
    )

    const handlerSipPlay = useCallback(
        () => rpc.IncomingOuterPanelRing(selectedSound?.value, "play").catch(failure),
        [failure, rpc, selectedSound?.value]
    )
    const handlerBellPlay = useCallback(
        () => rpc.IncomingFlatFrontRing(selectedSound2?.value, "play").catch(failure),
        [failure, rpc, selectedSound2?.value]
    )
    const setSoundReverseCheckbox = useCallback(() => setSoundReverse(p => !p), [])
    const onIntercomChange = useCallback((value: number) => setIntercomLevel(value), [])
    const onAlarmChange = useCallback((value: number) => setSoundAlarmLevel(value), [])
    const onIncomingCallFlatFrontChange = useCallback((value: number) => setSoundIncomingCallFlatFrontLevel(value), [])
    const onIncomingCallOutsidePanelChange = useCallback((value: number) => setSoundIncomingCallOutsidePanel(value), [])
    const onMicrophoneLevelChange = useCallback((value: number) => setMicrophoneLevel(value), [])

    const onClickSave = useCallback(() => {
        if (selectedSound == null || selectedSound2 == null) {
            failure('pls_select_a_ringtone')
            return
        }
        question(t("are_you_sure"), () => {
            const state = {
                ...sound,
                click_sound: soundReverse,
                intercom_level: intercomLevel,
                alarm_level: soundAlarmLevel,
                microphone_level: microphoneLevel,
                incoming_call_flat_front_level: soundIncomingCallFlatFrontLevel,
                incoming_call_outside_panel_level: soundIncomingCallOutsidePanel
            }
            rpc.updateSoundState(state)
                .then(() => dispatch(setSoundStateAction(state)))
                .catch(failure)
            rpc.SetClickSound(soundReverse)
            rpc.IncomingOuterPanelRing(selectedSound?.value, "save").catch(failure)
            rpc.IncomingFlatFrontRing(selectedSound2?.value, "save").catch(failure)
            setBellOption(selectedSound)
            setBellOption2(selectedSound2)
            success(t("this_successfully_edited"))
        })
    }, [
        t,
        question,
        success,
        dispatch,
        failure,
        setBellOption,
        setBellOption2,
        sound,
        rpc,
        soundReverse,
        soundIncomingCallOutsidePanel,
        soundAlarmLevel,
        microphoneLevel,
        soundIncomingCallFlatFrontLevel,
        intercomLevel,
        selectedSound,
        selectedSound2
    ])

    const buttons = useMemo(
        () => [
            {
                text: t("save"),
                className: "green",
                onClick: onClickSave
            }
        ],
        [t, onClickSave]
    )

    useEffect(() => {
        setSoundReverse(sound?.click_sound)
        setIntercomLevel(sound?.intercom_level)
        setMicrophoneLevel(sound?.microphone_level)
        setSoundIncomingCallFlatFrontLevel(sound?.incoming_call_flat_front_level)
        setSoundIncomingCallOutsidePanel(sound?.incoming_call_outside_panel_level)
        setSoundAlarmLevel(sound?.alarm_level)
        setSelectedSound(bellOption)
        setSelectedSound2(bellOption2)
    }, [sound, bellOption, bellOption2])

    return (
        <PasswordContainer requiredRole="default" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings"}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <RangeComponent
                            columnProps={{min: 12}}
                            name={t("incoming_call_outer_panel")}
                            value={soundIncomingCallOutsidePanel}
                            onChange={onIncomingCallOutsidePanelChange}
                            activeImage={iconSound}
                            passiveImage={iconSoundOff}
                        />
                        <RangeComponent
                            columnProps={{min: 12}}
                            name={t("incoming_call_flat_front")}
                            value={soundIncomingCallFlatFrontLevel}
                            onChange={onIncomingCallFlatFrontChange}
                            activeImage={iconSound}
                            passiveImage={iconSoundOff}
                        />
                        <RangeComponent
                            columnProps={{min: 12}}
                            name={t("intercom_call_voice")}
                            value={intercomLevel}
                            onChange={onIntercomChange}
                            activeImage={iconSound}
                            passiveImage={iconSoundOff}
                        />
                        <RangeComponent
                            columnProps={{min: 12}}
                            name={t("alarm_ring_volume")}
                            value={soundAlarmLevel}
                            onChange={onAlarmChange}
                            activeImage={iconSound}
                            passiveImage={iconSoundOff}
                        />
                    </div>
                    <div className="content-wrapper">
                        <div className="content-select-wrapper">
                            <div className="ring-tone-container">{t("incoming_call_outer_panel_ring")}</div>
                            <div className="ring-tone-select-container">
                                <div className="ring-tone-play-container" onClick={handlerSipPlay}>
                                    <img src={playSound} alt={playSound} />
                                </div>
                                <SettingSelectComponent
                                    label={t("")}
                                    options={incoming_call_flat_front}
                                    value={selectedSound}
                                    onChange={setSelectedSound}
                                />
                                <div className="ring-tone-save-container" style={{visibility: "hidden"}}>
                                    <img src={saveSound} alt={saveSound} />
                                </div>
                            </div>
                        </div>
                        <div className="content-select-wrapper">
                            <div className="ring-tone-container">{t("incoming_call_flat_front_ring")}</div>
                            <div className="ring-tone-select-container">
                                <div className="ring-tone-play-container" onClick={handlerBellPlay}>
                                    <img src={playSound} alt={playSound} />
                                </div>
                                <SettingSelectComponent
                                    label={t("")}
                                    options={incoming_call_outer_panel}
                                    value={selectedSound2}
                                    onChange={setSelectedSound2}
                                />
                                <div className="ring-tone-save-container" style={{visibility: "hidden"}}>
                                    <img src={saveSound} alt={saveSound} />
                                </div>
                            </div>
                        </div>
                        <RangeComponent
                            columnProps={{min: 12}}
                            name={t("microphone_level")}
                            value={microphoneLevel}
                            onChange={onMicrophoneLevelChange}
                            activeImage={microphoneOn}
                            passiveImage={microphoneOff}
                        />
                        <div className="click-voices-container">
                            <div className="click-voices-title">{t("click_sound")}</div>
                            <div className="click-voices-checkbox">
                                <SettingCheckBoxComponent
                                    label={""}
                                    set={setSoundReverseCheckbox}
                                    value={soundReverse}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
